var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { getNavigatorConnectionData, safeNumber, safeString, Stopwatch, Tracker } from "../helpers";
import { getFunctionHandler, telemetry } from "../constants";
import { formatCashOutTrackedException } from "./formatCashOutTrackedException";
import { loadCorrectiveActionsV2 } from "@draftkings/dk-ident-integration";
export class CashOutConnection {
    constructor(options) {
        this.send = (message) => {
            if (typeof this.subscriber !== "undefined") {
                this.messagesMap[message.method](message);
            }
        };
        this.executeRequest = (message) => {
            const start = performance.now();
            Tracker.getInstance()
                .trackEvent(telemetry.network[message.method].request, {
                Method: message.method,
                ClientType: telemetry.clientType,
                MyBetsVersion: telemetry.version,
            });
            if (message.method === "cashOutBet") {
                const betId = message.params.cashOutInformation.betId;
                Tracker.getInstance()
                    .trackEvent(telemetry.events.cashOutProcessing, Object.assign({ Action: telemetry.actions.cashOut.attempt, Method: message.method, ClientType: telemetry.clientType, MyBetsVersion: telemetry.version, ResponseBetId: betId, PurchaseId: message.params.cashOutInformation.purchaseId }, getNavigatorConnectionData()), betId);
            }
            // we've already checked and made sure that the params are of the corresponding type,
            // however the type generated could be any of the respective param types
            // so it sounds more reasonable to cast it as any, then to typecheck
            // eslint-disable-next-line
            this.cashOut.restClient[message.method](message.params)
                .then(response => {
                if (message.method === "cashOutBet") {
                    this.retryEnabled = true;
                }
                Tracker.getInstance()
                    .trackEvent(telemetry.network[message.method].response, Object.assign({ traceTimeInMilliseconds: performance.now() - start, Method: message.method, ClientType: telemetry.clientType, MyBetsVersion: telemetry.version }, getNavigatorConnectionData()));
                this.mapOnDataTracking[message.method][response.status](response);
                this.subscriber && this.subscriber.onData({
                    type: message.method,
                    data: response,
                });
            })
                .catch((error) => __awaiter(this, void 0, void 0, function* () {
                if (message.method === "acceptNewOffer") {
                    error.betId = message.params.betId;
                }
                if (message.method === "cashOutBet") {
                    if (error.statusCode === "Wager-006" && this.retryEnabled) {
                        yield loadCorrectiveActionsV2(window.dkConfig.draftkings.myAccountHost);
                        yield window.DKCorrectiveActionsV2.correctiveActionsInit({
                            siteExperience: this.siteExperience,
                            permission: "PlaySportsbook",
                            isModal: true
                        });
                        this.retryEnabled = false;
                        return this.executeRequest(message);
                    }
                    error.betId = message.params.cashOutInformation.betId;
                }
                Tracker.getInstance()
                    .trackEvent(telemetry.network[message.method].exception, formatCashOutTrackedException(error, start, message.method));
                this.mapOnErrorTracking[message.method](error);
                this.subscriber && this.subscriber.onError({
                    type: message.method,
                    data: error,
                });
            }));
        };
        this.handleCashOutBetAcceptedResultTracking = (operation) => {
            this.traceCashOutRequestEnd(operation);
            this.trackCashOutProcessing(operation, telemetry.actions.cashOut.immediateSuccess);
            this.traceCashOutFlowEnd(operation, "cashOutBet");
        };
        this.handleCashOutBetDeclinedResultTracking = (operation) => {
            this.traceCashOutRequestEnd(operation);
            this.trackCashOutProcessing(operation, telemetry.actions.cashOut.failure);
            this.traceCashOutFlowEnd(operation, "cashOutBet");
        };
        this.handleCashOutBetDelayResultTracking = (operation) => {
            this.traceCashOutRequestEnd(operation);
            this.handleOperationUpdateDelayResultTracking(operation);
        };
        this.handleCashOutBetNewOfferResultTracking = (operation) => {
            this.traceCashOutRequestEnd(operation);
            this.trackNewOfferReceived(operation, "cashOutBet");
        };
        this.handleAcceptNewOfferAcceptedResultTracking = (operation) => {
            this.traceNewOfferRequestEnd(operation);
            this.traceNewOfferFlowEnd(operation, "acceptNewOffer");
            this.trackCashOutProcessing(operation, telemetry.actions.cashOut.delayedSuccess);
            this.traceCashOutFlowEnd(operation, "acceptNewOffer");
        };
        this.handleAcceptNewOfferDeclinedResultTracking = (operation) => {
            this.traceNewOfferRequestEnd(operation);
            this.traceNewOfferFlowEnd(operation, "acceptNewOffer");
            this.trackCashOutProcessing(operation, telemetry.actions.cashOut.delayedFailure);
            this.traceCashOutFlowEnd(operation, "acceptNewOffer");
        };
        this.handleAcceptNewOfferDelayResultTracking = (operation) => {
            this.traceNewOfferRequestEnd(operation);
            this.traceNewOfferFlowEnd(operation, "acceptNewOffer");
            this.handleOperationUpdateDelayResultTracking(operation);
        };
        this.handleAcceptNewOfferResultTracking = (operation) => {
            this.traceNewOfferRequestEnd(operation);
            this.traceNewOfferFlowEnd(operation, "acceptNewOffer");
            this.trackNewOfferReceived(operation, "acceptNewOffer");
        };
        this.handleOperationUpdateDelayResultTracking = (operation) => {
            this.trackCashOutProcessing(operation, telemetry.actions.cashOut.beginDelay);
        };
        this.handleCashOutBetErrorTracking = (error) => {
            Tracker.getInstance()
                .trackEvent(telemetry.events.customTrace, {
                Trace: telemetry.trace.request,
                traceTimeInMilliseconds: Stopwatch.getElapsedTime(`${telemetry.trace.cashOutFlow}:${error.betId}`),
                Status: "Error",
                ResponseBetId: safeString(error.betId),
                ClientType: telemetry.clientType,
                MyBetsVersion: telemetry.version,
            })
                .trackEvent(telemetry.events.customTrace, {
                Trace: telemetry.trace.cashOutFlow,
                traceTimeInMilliseconds: Stopwatch.getElapsedTime(`${telemetry.trace.cashOutFlow}:${error.betId}`),
                ResponseBetId: safeString(error.betId),
                Status: "Error",
                ClientType: telemetry.clientType,
                MyBetsVersion: telemetry.version,
                Method: "cashOutBet",
            }, error.betId)
                .clearEventData(safeString(error.betId));
            Stopwatch.reset(`${telemetry.trace.cashOutFlow}:${error.betId}`);
        };
        this.handleAcceptNewOfferErrorTracking = (error) => {
            Tracker.getInstance()
                .trackEvent(telemetry.events.customTrace, {
                Trace: telemetry.trace.newOfferRequest,
                traceTimeInMilliseconds: Stopwatch.getElapsedTime(`${telemetry.trace.newOfferFlow}:${error.betId}`),
                Status: "Error",
                ResponseBetId: safeString(error.betId),
                ClientType: telemetry.clientType,
                MyBetsVersion: telemetry.version,
            }, error.betId)
                .trackEvent(telemetry.events.customTrace, {
                Trace: telemetry.trace.newOfferFlow,
                traceTimeInMilliseconds: Stopwatch.getElapsedTime(`${telemetry.trace.newOfferFlow}:${error.betId}`),
                Status: "Error",
                ResponseBetId: safeString(error.betId),
                ClientType: telemetry.clientType,
                MyBetsVersion: telemetry.version,
            }, error.betId);
            Stopwatch.reset(`${telemetry.trace.newOfferFlow}:${error.betId}`);
        };
        this.traceCashOutFlowEnd = (operation, method) => {
            const traceTimerId = `${telemetry.trace.cashOutFlow}:${operation.cashOutOperation.cashOutInformation.betId}`;
            const betId = operation.cashOutOperation.cashOutInformation.betId;
            if (Stopwatch.isRunning(traceTimerId)) {
                Tracker.getInstance()
                    .trackEvent(telemetry.events.customTrace, {
                    Trace: telemetry.trace.cashOutFlow,
                    traceTimeInMilliseconds: Stopwatch.getElapsedTime(traceTimerId),
                    Status: operation.status,
                    StatusUpdateTime: safeString(operation.statusUpdateTime),
                    ResponseBetId: operation.cashOutOperation.cashOutInformation.betId,
                    PurchaseId: operation.cashOutOperation.cashOutInformation.purchaseId,
                    ClientType: telemetry.clientType,
                    MyBetsVersion: telemetry.version,
                    Method: method,
                }, betId)
                    .clearEventData(betId);
                Stopwatch.reset(traceTimerId);
            }
        };
        this.traceCashOutRequestEnd = (operation) => {
            const betId = operation.cashOutOperation.cashOutInformation.betId;
            const traceTimerId = `${telemetry.trace.cashOutFlow}:${betId}`;
            Tracker.getInstance()
                .trackEvent(telemetry.events.customTrace, {
                Trace: telemetry.trace.request,
                traceTimeInMilliseconds: Stopwatch.getElapsedTime(traceTimerId),
                Status: operation.status,
                StatusUpdateTime: safeString(operation.statusUpdateTime),
                ResponseBetId: betId,
                PurchaseId: operation.cashOutOperation.cashOutInformation.purchaseId,
                MyBetsVersion: telemetry.version,
                ClientType: telemetry.clientType,
            }, betId);
        };
        this.trackCashOutProcessing = (operation, action) => {
            const betId = operation.cashOutOperation.cashOutInformation.betId;
            const limitDelay = safeNumber(operation.delayPeriod);
            Tracker.getInstance()
                .trackEvent(telemetry.events.cashOutProcessing, {
                Action: action,
                Status: operation.status,
                ResponseBetId: betId,
                PurchaseId: operation.cashOutOperation.cashOutInformation.purchaseId,
                ClientType: telemetry.clientType,
                LimitDelay: limitDelay,
                IsSuccess: operation.status === "Accepted",
                IsResult: operation.status === "Accepted" || operation.status === "Declined",
                IsFailure: operation.status === "Declined",
                MyBetsVersion: telemetry.version,
            }, betId);
        };
        this.traceNewOfferRequestEnd = (operation) => {
            const betId = operation.cashOutOperation.cashOutInformation.betId;
            const traceTimerId = `${telemetry.trace.newOfferFlow}:${betId}`;
            Tracker.getInstance()
                .trackEvent(telemetry.events.customTrace, {
                Trace: telemetry.trace.newOfferRequest,
                traceTimeInMilliseconds: Stopwatch.getElapsedTime(traceTimerId),
                Status: operation.status,
                ResponseBetId: betId,
                PurchaseId: operation.cashOutOperation.cashOutInformation.purchaseId,
                StatusUpdateTime: safeString(operation.statusUpdateTime),
                ClientType: telemetry.clientType,
                MyBetsVersion: telemetry.version,
            }, betId);
        };
        this.traceNewOfferFlowEnd = (operation, method) => {
            const betId = operation.cashOutOperation.cashOutInformation.betId;
            const traceTimerId = `${telemetry.trace.newOfferFlow}:${betId}`;
            if (Stopwatch.isRunning(traceTimerId)) {
                Tracker.getInstance()
                    .trackEvent(telemetry.events.customTrace, {
                    Trace: telemetry.trace.newOfferFlow,
                    traceTimeInMilliseconds: Stopwatch.getElapsedTime(traceTimerId),
                    Status: operation.status,
                    ResponseBetId: betId,
                    PurchaseId: operation.cashOutOperation.cashOutInformation.purchaseId,
                    StatusUpdateTime: safeString(operation.statusUpdateTime),
                    Method: method,
                    ClientType: telemetry.clientType,
                    MyBetsVersion: telemetry.version,
                }, betId);
                Stopwatch.reset(traceTimerId);
            }
        };
        this.trackNewOfferReceived = (operation, method) => {
            const betId = operation.cashOutOperation.cashOutInformation.betId;
            const newOfferTraceTimerId = `${telemetry.trace.newOfferFlow}:${betId}`;
            Stopwatch.start(newOfferTraceTimerId);
            Tracker.getInstance()
                .trackEvent(telemetry.events.cashOut, {
                Action: telemetry.actions.cashOut.newOfferReceived,
                Page: telemetry.page,
                Label: betId,
                NewOfferAmount: operation.cashOutOperation.cashOutAmount,
                NewOfferId: safeString(operation.newOfferId),
                ClientType: telemetry.clientType,
                Component: "CashOutView",
                Method: method,
                MyBetsVersion: telemetry.version,
            }, betId);
        };
        this.cashOut = {
            restClient: options.client,
        };
        this.messagesMap = new Proxy({
            cashOutBet: this.executeRequest,
            acceptNewOffer: this.executeRequest,
        }, getFunctionHandler);
        this.mapOnDataTracking = {
            cashOutBet: new Proxy({
                Accepted: this.handleCashOutBetAcceptedResultTracking,
                Declined: this.handleCashOutBetDeclinedResultTracking,
                LiveDelay: this.handleCashOutBetDelayResultTracking,
                TraderDelay: this.handleCashOutBetDelayResultTracking,
                NewOffer: this.handleCashOutBetNewOfferResultTracking,
            }, getFunctionHandler),
            acceptNewOffer: new Proxy({
                Accepted: this.handleAcceptNewOfferAcceptedResultTracking,
                Declined: this.handleAcceptNewOfferDeclinedResultTracking,
                LiveDelay: this.handleAcceptNewOfferDelayResultTracking,
                TraderDelay: this.handleAcceptNewOfferDelayResultTracking,
                NewOffer: this.handleAcceptNewOfferResultTracking,
            }, getFunctionHandler),
        };
        //For error tracking only, custom handlers, need to be defined, all requests are monitored by default
        this.mapOnErrorTracking = new Proxy({
            cashOutBet: this.handleCashOutBetErrorTracking,
            acceptNewOffer: this.handleAcceptNewOfferErrorTracking,
        }, getFunctionHandler);
        this.subscriber = undefined;
        this.siteExperience = options.siteExperience;
        this.retryEnabled = true;
    }
    subscribe(query) {
        this.subscriber = {
            onData: query.onData,
            onError: query.onError,
        };
        return {
            dispose: () => {
                this.subscriber = undefined;
            },
            send: this.send,
        };
    }
}
//# sourceMappingURL=CashOutConnection.js.map